import { DatadogClient } from '#util/datadog'

import {
  KNOWN_SERVERS,
  KnownServer,
  NamedServerConfig,
  ServerConfig,
} from './types'

const ddLog = DatadogClient.getInstance()

const PRODUCTION_MIXPANEL_TOKEN = '5f19e7b02416d724c5f7713034ac1f8f'
const STAGING_MIXPANEL_TOKEN = '254c5914299dd6c26d474fb3ea8aa02c'
const SLACK_CLIENT_ID = '2166313684.1525898859411'
const LOCAL_SLACK_CLIENT_ID = '2166313684.1988920550002'
type KnownHostedServer = Exclude<KnownServer, 'local'>

function isKnownServer(value: string): value is KnownHostedServer {
  return KNOWN_SERVERS.includes(value as KnownServer)
}
// NOTE: no intercom value is set. It's either on/off and handled
// in config.js during the build process
const LOCAL: ServerConfig = {
  API_URL: 'http://localhost:3004',
  NIDO_URL: 'http://localhost:3008',
  LOG_LEVEL: 'info',
  MIXPANEL_TOKEN: '',
  NODE_ENV: 'development',
  SLACK_CLIENT_ID: LOCAL_SLACK_CLIENT_ID,
  HTTP_TIMEOUT_MS: 30_000,
  // if you are using real stytch with a localhost server, you need to set up
  // a stytch project and populate the key here
  STYTCH_PUBLIC_TOKEN: process.env.REACT_APP_STYTCH_PUBLIC_TOKEN || null,
  STRIPE_PUBLISHABLE_KEY: process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || null,
  STRIPE_PRICING_TABLE_ID:
    process.env.REACT_APP_STRIPE_PRICING_TABLE_ID || null,
  STRIPE_RESUBSCRIBE_PRICING_TABLE_ID:
    process.env.REACT_APP_STRIPE_RESUBSCRIBE_PRICING_TABLE_ID || null,
  STRIPE_ACCOUNT_ID: process.env.REACT_APP_STRIPE_ACCOUNT_ID || null,
}

const HOSTED_SERVER_CONFIGS: Record<
  KnownHostedServer,
  Omit<ServerConfig, 'NODE_ENV'>
> = {
  production: {
    API_URL: 'https://api.production.scripto.live',
    NIDO_URL: 'https://nido.production.scripto.live',
    MIXPANEL_TOKEN: PRODUCTION_MIXPANEL_TOKEN,
    SLACK_CLIENT_ID,
    LOG_LEVEL: 'info',
    STYTCH_PUBLIC_TOKEN:
      'public-token-live-ef174cd0-ebec-4e90-a4d1-2a2638a1f524',
    STRIPE_PUBLISHABLE_KEY:
      'pk_live_51U344eHP5bYweUcSfjHvF8V8WNhYqQBlTmLEHxQiRYUKiY8bNjQqEYmoKVWnzIy3mO464szwdNLXEkvwU61AbJKz00FCbOTVyp',
    STRIPE_PRICING_TABLE_ID: 'prctbl_1UBdxJHP5bYweUcShKjOkPiF',
    STRIPE_RESUBSCRIBE_PRICING_TABLE_ID: 'prctbl_1USImDHP5bYweUcS78K0VvqN',
    STRIPE_ACCOUNT_ID: 'acct_1U344eHP5bYweUcS',
  },
  staging: {
    API_URL: 'https://api.staging.scripto.live',
    NIDO_URL: 'https://nido.staging.scripto.live',
    MIXPANEL_TOKEN: STAGING_MIXPANEL_TOKEN,
    SLACK_CLIENT_ID,
    LOG_LEVEL: 'info',
    STYTCH_PUBLIC_TOKEN:
      'public-token-test-af9e1710-ba0c-4fae-8149-d5e75df996e6',
    STRIPE_PUBLISHABLE_KEY:
      'pk_test_51U8HqCHubnhALk4JUn3V6cHyzNvSWcayxnV5VAC3WlzZFS8fpNtVthldKL900OOUsxJzJgqZ9FpyASf4UJYCKpYv00Oc6l608M',
    STRIPE_PRICING_TABLE_ID: 'prctbl_1UArjGHubnhALk4JAF1OWXxT',
    STRIPE_RESUBSCRIBE_PRICING_TABLE_ID: 'prctbl_1USISfHubnhALk4JsOI0OVmE',
    STRIPE_ACCOUNT_ID: 'acct_1U8HqCHubnhALk4J',
  },
  skatepark: {
    API_URL: 'https://api.skatepark.scripto.live',
    NIDO_URL: 'https://nido.skatepark.scripto.live',
    MIXPANEL_TOKEN: STAGING_MIXPANEL_TOKEN,
    SLACK_CLIENT_ID,
    LOG_LEVEL: 'info',
    STYTCH_PUBLIC_TOKEN:
      'public-token-test-cc562c90-d0eb-4c1b-a1d6-4addbad29e94',
    STRIPE_PUBLISHABLE_KEY:
      'pk_test_51U9OM2HXaTZdpbNlCTgoURA4HM1O0Ge8Z3vJTV1uRRjEvtTWW90iWNmP5mnLd8RTUBcbPsV9rbKE4nuaCLDkziPR00jCPCMZ5y',
    STRIPE_PRICING_TABLE_ID: 'prctbl_1UArsDHXaTZdpbNlyjGsMGtT',
    STRIPE_RESUBSCRIBE_PRICING_TABLE_ID: 'prctbl_1USIVzHXaTZdpbNljrQRmAmf',
    STRIPE_ACCOUNT_ID: 'acct_1U9OM2HXaTZdpbNl',
  },
  flanders: {
    API_URL: 'https://api.flanders.scripto.live',
    NIDO_URL: 'https://nido.flanders.scripto.live',
    MIXPANEL_TOKEN: STAGING_MIXPANEL_TOKEN,
    SLACK_CLIENT_ID,
    LOG_LEVEL: 'info',
    STYTCH_PUBLIC_TOKEN:
      'public-token-test-f25e9090-7f54-47b5-855a-2ec505a66e38',
    STRIPE_PUBLISHABLE_KEY:
      'pk_test_51U8ojWHuA4z6uhJhRbk2AQpaj1iKARD1tgZtXDMJx5DDlAw6TeEl8cqkBiJPR6WgiPX4Mve7Kk2r3kHkznxAe3kS00wuPEhPyO',
    STRIPE_PRICING_TABLE_ID: 'prctbl_1UAro3HuA4z6uhJhDINwKzHT',
    STRIPE_RESUBSCRIBE_PRICING_TABLE_ID: 'prctbl_1USIG2HuA4z6uhJheWWtArxQ',
    STRIPE_ACCOUNT_ID: 'acct_1U8ojWHuA4z6uhJh',
  },
  'alex-dev': {
    API_URL: 'https://api.alex-dev.scripto.live',
    NIDO_URL: 'https://nido.alex-dev.scripto.live',
    MIXPANEL_TOKEN: STAGING_MIXPANEL_TOKEN,
    SLACK_CLIENT_ID,
    LOG_LEVEL: 'info',
    HTTP_TIMEOUT_MS: 1_000,
    STYTCH_PUBLIC_TOKEN:
      'public-token-live-00c73ca7-c4b4-4dc5-a434-ac96277bd3be',
    STRIPE_PUBLISHABLE_KEY:
      'pk_test_51UASCgHKBsk19Rg0BKr9n6AXQ4ffkipSdsJkxgtbpZWh9ktcURSBi1OAfGwplb50d4xjz0JsCjXUgZvGQPWtOBo900y1EQUrNL',
    STRIPE_PRICING_TABLE_ID: 'prctbl_1UArwYHKBsk19Rg04JvWZVZa',
    STRIPE_RESUBSCRIBE_PRICING_TABLE_ID: 'prctbl_1USIYkHKBsk19Rg0B20raxtV',
    STRIPE_ACCOUNT_ID: 'acct_1UASCgHKBsk19Rg0',
  },
}

const localProxyConfig = (environment: KnownHostedServer): ServerConfig => {
  const {
    API_URL,
    NIDO_URL,
    STYTCH_PUBLIC_TOKEN,
    STRIPE_PUBLISHABLE_KEY,
    STRIPE_PRICING_TABLE_ID,
    STRIPE_RESUBSCRIBE_PRICING_TABLE_ID,
    STRIPE_ACCOUNT_ID,
  } = HOSTED_SERVER_CONFIGS[environment]
  return {
    ...LOCAL,
    API_URL,
    NIDO_URL,
    STYTCH_PUBLIC_TOKEN,
    STRIPE_PUBLISHABLE_KEY:
      STRIPE_PUBLISHABLE_KEY ?? LOCAL.STRIPE_PUBLISHABLE_KEY,
    STRIPE_PRICING_TABLE_ID:
      STRIPE_PRICING_TABLE_ID ?? LOCAL.STRIPE_PRICING_TABLE_ID,
    STRIPE_RESUBSCRIBE_PRICING_TABLE_ID:
      STRIPE_RESUBSCRIBE_PRICING_TABLE_ID ??
      LOCAL.STRIPE_RESUBSCRIBE_PRICING_TABLE_ID,
    STRIPE_ACCOUNT_ID: STRIPE_ACCOUNT_ID ?? LOCAL.STRIPE_ACCOUNT_ID,
  }
}

const getConfig = (environment: KnownServer): ServerConfig => {
  if (environment === 'local') {
    return LOCAL
  }

  // process.env.NODE_ENV is set at build time by React
  if (process.env.NODE_ENV === 'development') {
    return localProxyConfig(environment)
  }

  return {
    ...HOSTED_SERVER_CONFIGS[environment],
    NODE_ENV: 'production',
  }
}

// if you're hitting from a known url like https://staging.scripto.dev
// or http://localhost:3004 this will find the valid hostname
const getKnownServerFromHostname = (
  hostname: string,
): KnownServer | undefined => {
  const hostPart = hostname.split('.')[0]
  if (isKnownServer(hostPart)) {
    return hostPart
  }
  if (hostPart === 'localhost') {
    return 'local'
  }
}

const getBuildTimeOverride = (): KnownServer | undefined => {
  const envValue = window.scriptoEnvironment
  if (typeof envValue === 'string' && isKnownServer(envValue)) {
    return envValue
  }
}

export const getServerConfig = (): NamedServerConfig => {
  const environment: KnownServer | undefined =
    getBuildTimeOverride() ??
    getKnownServerFromHostname(window.location.hostname)

  if (!environment) {
    if (process.env.NODE_ENV !== 'production') {
      throw new Error('Invalid server configuration')
    }

    ddLog.error('Scripto server environment misconfigured', {
      hostname: window.location.hostname,
      build: getBuildTimeOverride(),
    })
    // If we wind up in this situation, it's a bug or bad deployment, but
    // let's opt for production as the best possible fallback for end users
    return { ...getConfig('production'), name: 'production' }
  }

  return { ...getConfig(environment), name: environment }
}
